import React from 'react';
import { Flame, Anvil, Trophy } from 'lucide-react';

/**
 * ForgeHeader - Sticky top header with progress summary
 */
export default function ForgeHeader({ 
  courseName = "Foundations",
  wordRange = "1-500",
  blacksmith,
  totalGoldLessons = 0,
  nextRankAt = 10,
  totalScore = 0
}) {
  const progressPercent = Math.min(100, (totalGoldLessons % 10) * 10);

  return (
    <header className="sticky top-0 z-30 w-full">
      {/* Main header bar */}
      <div className="bg-gradient-to-b from-slate-900/98 via-slate-900/95 to-slate-900/90 backdrop-blur-md border-b border-amber-900/30 shadow-lg shadow-black/20">
        <div className="max-w-2xl mx-auto px-4 py-3">
          {/* Top row - Title and Blacksmith card */}
          <div className="flex items-center justify-between gap-4">
            {/* Title section */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-600 to-orange-700 flex items-center justify-center shadow-lg shadow-amber-600/30">
                  <Flame className="h-5 w-5 text-white" />
                </div>
                <div className="absolute inset-0 rounded-lg bg-amber-500/20 blur-md -z-10" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-amber-100 tracking-tight">
                  Forge Path
                </h1>
                <p className="text-xs text-amber-300/60">
                  {courseName} • Words {wordRange}
                </p>
              </div>
            </div>

            {/* Score and Blacksmith section */}
            <div className="flex items-center gap-3">
              {/* Total Score Display */}
              <div className="flex items-center gap-2 bg-gradient-to-r from-amber-900/50 to-amber-800/30 border border-amber-600/30 rounded-lg px-3 py-2" data-testid="total-score-display">
                <Trophy className="h-4 w-4 text-amber-400" />
                <div className="text-right">
                  <p className="text-[9px] text-amber-300/50 uppercase tracking-wider font-medium">Score</p>
                  <p className="text-sm font-bold text-amber-400">{totalScore.toLocaleString()}</p>
                </div>
              </div>

              {/* Blacksmith rank card */}
              {blacksmith && (
                <div className="flex items-center gap-3 bg-gradient-to-r from-slate-800/90 to-slate-800/70 border border-amber-700/30 rounded-xl px-3 py-2 shadow-lg" data-testid="blacksmith-container">
                  <div className="relative">
                    <img 
                      src={blacksmith.image_url} 
                      alt={blacksmith.rank_display}
                      className="h-12 w-12 object-contain"
                      style={{ 
                        filter: 'drop-shadow(0 2px 8px rgba(251, 191, 36, 0.3))'
                      }}
                    />
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-amber-300/50 uppercase tracking-wider font-medium">Rank</p>
                    <p className="text-sm font-bold text-amber-400">{blacksmith.rank_display}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between text-[10px] text-amber-300/60 mb-1.5 font-medium">
              <span className="flex items-center gap-1">
                <Anvil className="h-3 w-3" />
                Gold Lessons: {totalGoldLessons}
              </span>
              <span>Next rank at {nextRankAt}</span>
            </div>
            <div className="relative h-2 bg-slate-800/80 rounded-full overflow-hidden border border-amber-900/40">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-900/20 via-transparent to-amber-900/20" />
              
              {/* Progress fill */}
              <div 
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-400 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progressPercent}%` }}
              >
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
              </div>
              
              {/* Tick marks */}
              <div className="absolute inset-0 flex justify-between px-0.5">
                {[...Array(10)].map((_, i) => (
                  <div 
                    key={i} 
                    className="w-px h-full bg-slate-700/50"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom fade effect */}
      <div className="h-4 bg-gradient-to-b from-slate-900/50 to-transparent pointer-events-none" />
    </header>
  );
}
